import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { MoreHorizontal } from 'lucide-react';

export interface OverflowMenuItem {
  label: string;
  onClick: () => void;
  icon?: ReactNode;
  disabled?: boolean;
  danger?: boolean;
}

/** Distance kept between the panel and the viewport edges. */
const VIEWPORT_MARGIN = 8;
/** Gap between the trigger and the panel. */
const TRIGGER_GAP = 4;

export interface MenuPosition {
  top?: number;
  bottom?: number;
  left?: number;
  right?: number;
  maxHeight?: number;
}

/**
 * Place the panel next to the trigger, inside the viewport. Right-aligned under
 * the trigger by default; left-anchored when that would run off the left edge
 * (a ⋯ trigger at the start of a stacked mobile header); flipped above the
 * trigger when it doesn't fit below and there is more room above; height-capped
 * so every item can always be scrolled to.
 */
// eslint-disable-next-line react-refresh/only-export-components -- pure helper, unit-tested on its own
export function placeMenu(
  trigger: Pick<DOMRect, 'top' | 'bottom' | 'left' | 'right'>,
  menu: { width: number; height: number },
  viewport: { width: number; height: number },
): MenuPosition {
  const pos: MenuPosition = {};

  if (trigger.right - menu.width >= VIEWPORT_MARGIN) {
    pos.right = Math.max(VIEWPORT_MARGIN, viewport.width - trigger.right);
  } else {
    pos.left = Math.max(
      VIEWPORT_MARGIN,
      Math.min(trigger.left, viewport.width - menu.width - VIEWPORT_MARGIN),
    );
  }

  const spaceBelow = viewport.height - trigger.bottom - TRIGGER_GAP - VIEWPORT_MARGIN;
  const spaceAbove = trigger.top - TRIGGER_GAP - VIEWPORT_MARGIN;
  if (menu.height > spaceBelow && spaceAbove > spaceBelow) {
    pos.bottom = viewport.height - trigger.top + TRIGGER_GAP;
    pos.maxHeight = Math.max(0, spaceAbove);
  } else {
    pos.top = trigger.bottom + TRIGGER_GAP;
    pos.maxHeight = Math.max(0, spaceBelow);
  }
  return pos;
}

function samePosition(a: MenuPosition, b: MenuPosition) {
  return (
    a.top === b.top &&
    a.bottom === b.bottom &&
    a.left === b.left &&
    a.right === b.right &&
    a.maxHeight === b.maxHeight
  );
}

/**
 * A small hand-rolled dropdown (the project has no headless-menu library). Used
 * to tuck secondary actions behind a "⋯" trigger so the primary action row stays
 * uncluttered. Closes on item click, outside mousedown/touchstart, and Escape.
 * Accessible: the trigger owns aria-haspopup/aria-expanded and the panel uses
 * menu roles.
 *
 * The open panel is rendered into document.body via a portal and positioned
 * `fixed` from the trigger's bounding rect, so ancestors with `overflow-hidden`
 * (e.g. RowList's card clipping) or transforms can't clip it. Once mounted the
 * panel is measured and clamped into the viewport (`placeMenu`), so a trigger
 * near the left edge of a phone screen never pushes items off-screen.
 */
export function OverflowMenu({
  items,
  label = 'More actions',
  size = 'default',
}: {
  items: OverflowMenuItem[];
  label?: string;
  /** Match the size of adjacent action buttons. */
  size?: 'sm' | 'default';
}) {
  const [open, setOpen] = useState(false);
  const [position, setPosition] = useState<MenuPosition | null>(null);
  const rootRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onPointerDown(e: MouseEvent | TouchEvent) {
      const target = e.target as Node;
      const insideTrigger = rootRef.current?.contains(target);
      const insideMenu = menuRef.current?.contains(target);
      if (!insideTrigger && !insideMenu) {
        setOpen(false);
      }
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') setOpen(false);
    }
    function onScrollOrResize(e: Event) {
      // Scrolling the (height-capped) panel itself must not close it.
      if (e.target instanceof Node && menuRef.current?.contains(e.target)) return;
      setOpen(false);
    }
    document.addEventListener('mousedown', onPointerDown);
    document.addEventListener('touchstart', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      document.removeEventListener('touchstart', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
    };
  }, [open]);

  // Clamp the panel into the viewport using its measured size, before paint.
  useLayoutEffect(() => {
    if (!open || !buttonRef.current || !menuRef.current) return;
    const menu = menuRef.current.getBoundingClientRect();
    const next = placeMenu(
      buttonRef.current.getBoundingClientRect(),
      { width: menu.width, height: menu.height },
      { width: window.innerWidth, height: window.innerHeight },
    );
    setPosition((prev) => (prev && samePosition(prev, next) ? prev : next));
  }, [open]);

  function toggle() {
    if (!open && buttonRef.current) {
      const r = buttonRef.current.getBoundingClientRect();
      setPosition({ top: r.bottom + TRIGGER_GAP, right: window.innerWidth - r.right });
    }
    setOpen((o) => !o);
  }

  return (
    <div ref={rootRef} className="relative">
      <button
        ref={buttonRef}
        type="button"
        className={size === 'sm' ? 'btn-secondary btn-sm' : 'btn-secondary'}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label={label}
        onClick={toggle}
      >
        <MoreHorizontal className={size === 'sm' ? 'h-3.5 w-3.5' : 'h-4 w-4'} aria-hidden="true" />
      </button>

      {open &&
        position &&
        createPortal(
          <div
            ref={menuRef}
            role="menu"
            style={position}
            className="fixed z-50 min-w-44 overflow-y-auto overscroll-contain rounded-[8px] border border-line bg-surface-1 py-1 shadow-lg"
          >
            {items.map((item) => (
              <button
                key={item.label}
                type="button"
                role="menuitem"
                disabled={item.disabled}
                className={`flex w-full items-center gap-2 px-3 py-2 text-left text-sm disabled:opacity-50 ${
                  item.danger
                    ? 'text-danger hover:bg-danger-soft'
                    : 'text-ink hover:bg-surface-2'
                }`}
                onClick={() => {
                  setOpen(false);
                  item.onClick();
                }}
              >
                {item.icon && <span className="flex-none">{item.icon}</span>}
                {item.label}
              </button>
            ))}
          </div>,
          document.body,
        )}
    </div>
  );
}
